// ./src/hooks/useRooms.ts
import { useState, useCallback, useMemo, useEffect } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Room,
  RoomService,
  RoomFilter,
  RoomInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";

export interface UseRoomsOptions {
  /** Venue ID to filter rooms by */
  venueId?: string | null | undefined;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<RoomFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  userId?: string | null;
}

export interface UseRoomsReturn {
  // Data state
  room: Room | null;
  rooms: Room[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<RoomFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingRoomId: string | null;
  selectedRoom: Room | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<RoomFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Room[]>;
  create: (params: RoomInput) => Promise<Room | null>;
  update: (id: string, params: RoomInput) => Promise<Room | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<RoomFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedRoom: (room: Room | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  roomsByVenue: Record<string, Room[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_PAGE_SIZE = 200;
const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useRooms = (options: UseRoomsOptions = {}): UseRoomsReturn => {
  const {
    venueId,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    userId = null,
  } = options;

  // Data state
  const [room, setRoom] = useState<Room | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  // Search and filters
  const [filters, setFilters] = useState<Partial<RoomFilter>>(() => {
    const filter = { ...initialFilters };
    // Always include venueId if provided
    if (venueId) {
      filter.venueId = [venueId];
    }
    return filter;
  });
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingRoomId, setRemovingRoomId] = useState<string | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => rooms.length === 0 && !loading,
    [rooms.length, loading]
  );

  const roomsByVenue = useMemo(() => {
    return rooms.reduce((acc, room) => {
      const venueId = room.venueId || "unknown";
      if (!acc[venueId]) {
        acc[venueId] = [];
      }
      acc[venueId].push(room);
      return acc;
    }, {} as Record<string, Room[]>);
  }, [rooms]);

  // Helper function to update room in list if it exists, or add it if not
  const updateOrAddRoomInList = useCallback((updatedRoom: Room) => {
    setRooms((prev) => {
      const existingIndex = prev.findIndex((r) => r.id === updatedRoom.id);
      if (existingIndex >= 0) {
        // Update existing room
        const newRooms = [...prev];
        newRooms[existingIndex] = updatedRoom;
        return newRooms;
      } else {
        // Add new room
        return [updatedRoom, ...prev];
      }
    });
  }, []);

  // Helper function to remove room from list if it exists
  const removeRoomFromList = useCallback((roomId: string) => {
    setRooms((prev) => prev.filter((r) => r.id !== roomId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    console.log("Resetting all the rooms");
    setRoom(null);
    setRooms([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingRoomId(null);
    setSelectedRoom(null);
  }, []);

  // Get single room (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await RoomService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const roomData = result.data[0] as Room;
          setRoom(roomData);

          // Update or add room in the list
          updateOrAddRoomInList(roomData);

          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load room";
          setLastError(errorMessage);
          setRoom(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load room";
        setLastError(errorMessage);
        setRoom(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddRoomInList]
  );

  // List rooms (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<RoomFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSize;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setRooms([]);
          setCurrentPage(1);
        }
      }

      // Apply venueId filter if provided in options
      const combinedFilter = { ...filter };
      if (venueId && !combinedFilter.venueId) {
        combinedFilter.venueId = [venueId];
      }

      // Update internal state
      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = (sortByParams || sortBy).map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await RoomService.list(
          combinedFilter as RoomFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newRooms = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setRooms((prev) => {
              const existingIds = new Set(prev.map((r) => r.id));
              const uniqueNewRooms = newRooms.filter(
                (r) => !existingIds.has(r.id)
              );
              return [...prev, ...uniqueNewRooms];
            });
          } else {
            setRooms(newRooms);
          }

          setHasMore(newRooms.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load rooms";
          setLastError(errorMessage);
          if (!isAppending) setRooms([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load rooms";
        setLastError(errorMessage);
        if (!isAppending) setRooms([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize, sortBy, venueId]
  );

  // Get many rooms by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Room[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await RoomService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedRooms = Array.isArray(result.data) ? result.data : [];

          // Update or add each loaded room to the list
          loadedRooms.forEach((room) => {
            updateOrAddRoomInList(room);
          });

          setLastError(null);
          return loadedRooms;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load rooms";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load rooms";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddRoomInList]
  );

  // Create room (matching API method name)
  const create = useCallback(
    async (params: RoomInput): Promise<Room | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        // Ensure venueId is set if provided in options
        const roomToCreate = {
          ...params,
          venueId: params.venueId || venueId,
        };

        const result = await RoomService.create(roomToCreate);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newRoom = result.data[0] as Room;

          // Add to the beginning of rooms list
          setRooms((prev) => [newRoom, ...prev]);

          setLastError(null);
          return newRoom;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create room";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create room";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [venueId]
  );

  // Update room (matching API method name)
  const update = useCallback(
    async (id: string, params: RoomInput): Promise<Room | null> => {
      if (!id) {
        setLastError("Room ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await RoomService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedRoom = result.data[0] as Room;

          // Update single room if it matches
          if (room?.id === updatedRoom.id) {
            setRoom(updatedRoom);
          }

          // Update room in list if it exists
          updateOrAddRoomInList(updatedRoom);

          setLastError(null);
          return updatedRoom;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update room";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update room";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [room?.id, updateOrAddRoomInList]
  );

  // Remove room (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Room ID is required for removal");
        return false;
      }

      setRemovingRoomId(id);
      setLastError(null);

      try {
        const result = await RoomService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          // Clear single room if it matches
          if (room?.id === id) {
            setRoom(null);
          }

          // Remove from rooms list if it exists
          removeRoomFromList(id);

          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove room";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove room";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingRoomId(null);
      }
    },
    [room?.id, removeRoomFromList]
  );

  // Load more rooms
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(filters, { page: nextPage, pageSize }, sortBy, false);
    }
  }, [list, loadingMore, hasMore, currentPage, pageSize, filters, sortBy]);

  // Refresh rooms
  const refresh = useCallback(async () => {
    await list(filters, { page: 1, pageSize }, sortBy, true);
  }, [list, filters, pageSize, sortBy]);

  useEffect(() => {
    if (autoLoad && venueId && loadedForUserId != userId) {
      setLoadedForUserId(userId);
      // Use initial filters and sort for auto-load
      const autoLoadFilters = { ...initialFilters, venueId: [venueId] };

      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [
    autoLoad,
    userId,
    loadedForUserId,
    venueId,
    pageSize,
    initialFilters,
    initialSort,
    list,
  ]);

  return {
    // Data state
    room,
    rooms,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Pagination
    currentPage,
    pageSize,

    // Operations state
    isCreating,
    isUpdating,
    removingRoomId,
    selectedRoom,

    // Core operations (matching API method names exactly)
    get,
    list,
    getMany,
    create,
    update,
    remove,

    // Additional convenience methods
    loadMore,
    refresh,

    // Filter operations
    setFilters,
    setSortBy,

    // UI state management
    setSelectedRoom,

    // Helper operations
    reset,

    // Computed values
    roomsByVenue,

    // Error handling
    lastError,
    clearError,
  };
};
